import { Subscription } from "../../api/subscriptionApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  getInstrumentIcon,
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import { AlertCircle, Check, Clock, Euro, Repeat } from "lucide-react";
import { weekdays } from "../Weekdays.ts";
import { useLingui } from "@lingui/react/macro";
import { Trans } from "@lingui/react/macro";
import { Link } from "react-router";
import { ProfilePicture } from "../profile/ProfilePicture.tsx";

interface PendingSubscriptionCardProps {
  subscription: Subscription;
  onAccept: () => void;
  isAccepting: boolean;
}

export default function PendingSubscriptionCard({
  subscription,
  onAccept,
  isAccepting,
}: PendingSubscriptionCardProps) {
  const { language } = useLanguageStore();
  const { data: supportedInstruments } = useSupportedInstruments();
  const { i18n } = useLingui();

  const InstrumentIcon = getInstrumentIcon(subscription.instrument);
  const instrumentLabel = getTranslations(
    supportedInstruments,
    subscription.instrument,
    language,
  );

  const weekday = weekdays.find((w) => w.value === subscription.dayOfWeek);
  const weekdayLabel = weekday ? i18n._(weekday.label) : subscription.dayOfWeek;

  const fromDisplay = subscription.from.substring(0, 5);
  const toDisplay = subscription.to.substring(0, 5);

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-[--shadow-card]">
      {/* Header: instrument + pending badge */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <InstrumentIcon className="h-5 w-5 shrink-0 text-amber-700" />
          <span className="truncate text-sm font-semibold text-amber-900">
            {instrumentLabel}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
          <AlertCircle className="h-3 w-3" />
          <Trans>Pending</Trans>
        </div>
      </div>

      {/* Subscriber info */}
      {subscription.subscriberName && (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0">
            <ProfilePicture
              profilePictureUrl={subscription.subscriberProfilePictureUrl}
              className="h-9 w-9"
              iconSize={20}
            />
          </div>
          <Link
            to={`/profile/${subscription.subscriberId}`}
            className="truncate text-sm font-medium text-amber-900 hover:underline"
          >
            {subscription.subscriberName}
          </Link>
        </div>
      )}

      {/* Day + time range */}
      <div className="flex items-center gap-1.5 text-xs font-medium text-amber-900">
        <Clock className="h-3.5 w-3.5 shrink-0 opacity-60" />
        <span>
          {weekdayLabel}, {fromDisplay} – {toDisplay}
        </span>
      </div>

      {/* Monthly price */}
      {subscription.price !== undefined && (
        <div className="flex items-center gap-1.5 text-xs font-medium text-amber-900">
          <Euro className="h-3.5 w-3.5 shrink-0 opacity-60" />
          <span>
            {subscription.price.toFixed(2)} / <Trans>month</Trans>
          </span>
        </div>
      )}

      {/* Accept action */}
      <button
        onClick={onAccept}
        disabled={isAccepting}
        className="mt-1 flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-green-600 px-3 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-150 hover:bg-green-700 focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 focus-visible:outline-none active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100"
      >
        {isAccepting ? (
          <>
            <Repeat className="h-4 w-4 animate-spin" />
            <span>
              <Trans>Accepting…</Trans>
            </span>
          </>
        ) : (
          <>
            <Check className="h-4 w-4" />
            <span>
              <Trans>Accept Subscription</Trans>
            </span>
          </>
        )}
      </button>
    </div>
  );
}
